import type { LayoutDirection } from './types';

// ---------------------------------------------------------------------------
// Layout constants (legacy – kept for backward-compat; prefer FLOW_CANVAS_CONFIG)
// ---------------------------------------------------------------------------
export const LAYOUT = {
  FLOW_VERTICAL_SPACING: 200,
  CREDENTIAL_VERTICAL_DIVISOR: 1.5,
  CREDENTIAL_HORIZONTAL_OFFSET: 20,
  ZOOM_THRESHOLD: 0.75,
  READABILITY_ZOOM: 1,
} as const;

// ---------------------------------------------------------------------------
// Centralized Flow Canvas Configuration
//
// Two directional blocks (LR / TB) hold every tuneable spacing, position and
// sizing value used across the canvas. To adjust a layout behaviour, change
// the value here — no need to hunt through dagreLayout / nodeBuilders / etc.
// ---------------------------------------------------------------------------

type DirectionConfig = {
  credential: {
    /** Where the credential node sits relative to its source */
    position: 'bottom' | 'left' | 'top' | 'right';
    /** Gap between credential node and source */
    gap: number;
    /** Estimated credential node height for positioning */
    heightEstimate: number;
  };
  flowControl: {
    /** Where Run Now / Cron sits relative to the flow container */
    position: 'left' | 'top';
    /** Height of the flow-control banner node */
    height: number;
    /** Gap between flow-control node and flow container */
    gap: number;
    /** Estimated width of the flow-control node (used for LR offset) */
    widthEstimate: number;
    /** Gap between multiple control nodes (e.g. Cron + Run Now side by side) */
    controlNodeSpacing: number;
  };
  actionMenu: {
    /** Side of the card where the + action button appears */
    position: 'right' | 'bottom';
  };
  nexsetLoader: {
    /** Where the nexset-loader node sits relative to its source */
    position: 'right' | 'bottom';
  };
  secondaryFlow: {
    /** Default width of expanded secondary-flow containers */
    defaultWidth: number;
    /** Min width when collapsed */
    collapsedWidth: number;
    /** Extra width per branch beyond the first */
    widthPerBranch: number;
  };
  /**
   * Reserved vertical padding inside a flow-container's nodesContainer — the
   * header strip plus (in TB) the schedule-control row that sits above the
   * source. `FlowNode` / `SecondaryFlowNode` subtract this from `data.height`
   * to compute the inner content height.
   *
   * LR keeps the legacy 200; TB needs more vertical room for Run-Now / Cron
   * stacked above the source. Consumers must read this from the direction
   * block — do NOT hard-code a single value.
   */
  flowVerticalSpacing: number;
};

type SharedConfig = {
  zoomThreshold: number;
  readabilityZoom: number;
  secondaryFlowMargin: number;
  secondaryFlowPadding: number;
  secondaryFlowHeight: number;
  independentFlowSpacing: number;
  flowHeaderOffset: number;
  headerMinWidth: number;
  /** Run Now / Cron schedule nodes (canvas-level, like credentials) */
  scheduleControl: {
    zIndex: number;
  };
  flowContainer: {
    /** Default width when no child nodes exist */
    defaultWidth: number;
    /** Default height when no child nodes exist */
    defaultHeight: number;
    /** Horizontal padding added to child bounds for auto-sizing */
    paddingX: number;
    /** Vertical padding added to child bounds for auto-sizing */
    paddingY: number;
    /** Extra width per branch beyond the first (for multi-branch flows) */
    widthPerBranch: number;
  };
};

export const FLOW_CANVAS_CONFIG: {
  LR: DirectionConfig;
  TB: DirectionConfig;
  shared: SharedConfig;
} = {
  LR: {
    credential: {
      position: 'bottom',
      gap: 12,
      heightEstimate: 60,
    },
    flowControl: {
      position: 'left',
      height: 90,
      gap: 30,
      widthEstimate: 130,
      controlNodeSpacing: 15,
    },
    actionMenu: {
      position: 'right',
    },
    nexsetLoader: {
      position: 'right',
    },
    secondaryFlow: {
      defaultWidth: 500,
      collapsedWidth: 200,
      widthPerBranch: 320,
    },
    flowVerticalSpacing: 200,
  },
  TB: {
    credential: {
      position: 'left',
      gap: 12,
      heightEstimate: 60,
    },
    flowControl: {
      position: 'top',
      height: 90,
      gap: 30,
      widthEstimate: 130,
      controlNodeSpacing: 15,
    },
    actionMenu: {
      position: 'bottom',
    },
    nexsetLoader: {
      position: 'bottom',
    },
    secondaryFlow: {
      defaultWidth: 500,
      collapsedWidth: 200,
      widthPerBranch: 320,
    },
    flowVerticalSpacing: 300,
  },
  shared: {
    zoomThreshold: 0.75,
    readabilityZoom: 1,
    secondaryFlowMargin: 80,
    secondaryFlowPadding: 40,
    secondaryFlowHeight: 140,
    independentFlowSpacing: 80,
    flowHeaderOffset: 150,
    headerMinWidth: 700,
    scheduleControl: {
      zIndex: 2,
    },
    flowContainer: {
      defaultWidth: 700,
      defaultHeight: 500,
      paddingX: 60,
      paddingY: 60,
      widthPerBranch: 450,
    },
  },
};

// ---------------------------------------------------------------------------
// Dagre spacing by flow type × layout direction
//
// Each flow type defines LR and TB spacing explicitly (nodesep, ranksep, edgesep).
// Former TB ranksep used FLOW_CANVAS_CONFIG.LR/TB.spacing.ranksepFactor (0.25 on TB);
// those effective values are now inlined in the TB column below so tuning is local.
// ---------------------------------------------------------------------------

/** Dagre spacing tuple for one layout direction */
export type DagreSpacingConfig = {
  /** Spacing between nodes on the same rank */
  nodesep: number;
  /** Minimum separation between ranks (main axis for LR/TB) */
  ranksep: number;
  edgesep: number;
};

/** Per flow type: separate spacing for LR vs TB canvas layouts */
export type FlowTypeSpacingByDirection = {
  LR: DagreSpacingConfig;
  TB: DagreSpacingConfig;
};

export const FLOW_TYPE_SPACING: Record<string, FlowTypeSpacingByDirection> = {
  rag: {
    LR: { nodesep: 100, ranksep: 70, edgesep: 50 },
    TB: { nodesep: 100, ranksep: 50, edgesep: 50 },
  },
  api_server: {
    LR: { nodesep: -80, ranksep: 30, edgesep: 30 },
    TB: { nodesep: -80, ranksep: 20, edgesep: 30 },
  },
};

/**
 * Default Dagre spacing for **TB** (top→bottom) layout on generic flows.
 *
 * - **ranksep** — Minimum gap between ranks (vertical distance between pipeline
 *   stages). This is the primary knob for TB density; Dagre receives it as `ranksep`.
 * - **nodesep** — Gap between nodes on the same rank (horizontal separation in TB).
 * - **edgesep** — Edge routing separation.
 *
 * Specialized flows (`FLOW_TYPE_SPACING.rag`, `api_server`, …) override these.
 */
export const DAGRE_DEFAULT_TB_SPACING: DagreSpacingConfig = {
  nodesep: 20,
  ranksep: 16,
  edgesep: 10,
};

export const DEFAULT_FLOW_TYPE_SPACING: FlowTypeSpacingByDirection = {
  LR: { nodesep: -100, ranksep: 50, edgesep: 40 },
  TB: { ...DAGRE_DEFAULT_TB_SPACING },
};

// ---------------------------------------------------------------------------
// Per-rank spacing (post-dagre adjustment)
//
// `adjustPerRankSpacing` in dagreLayout.ts OVERRIDES dagre's `ranksep` with a
// fixed per-node-type value from this table. So this — not `ranksep` above —
// is the real lever for edge length between specific node-type transitions
// (e.g. data_source → data_set) in default/streaming flows.
//
// Value = gap (px) inserted AFTER a node of that type, before the next rank.
// Split by LR/TB so TB can be tighter (compensates for the smaller
// bounding-box width delta on vertical layouts).
// ---------------------------------------------------------------------------
export type RankSpacingByNodeType = Record<string, number>;
export type RankSpacingByDirection = {
  LR: RankSpacingByNodeType;
  TB: RankSpacingByNodeType;
};

export const RANK_SPACING_BY_FLOW_TYPE: Record<string, RankSpacingByDirection> = {
  api_server: {
    LR: {
      api_server: 200,
      api_request: 80,
      api_target: 60,
      api_success_modifier: 60,
      splitter_output: 60,
      splitter: 80,
      api_result: 80,
      api_error: 80,
    },
    TB: {
      api_server: 120,
      api_request: 50,
      api_target: 40,
      api_success_modifier: 40,
      splitter_output: 40,
      splitter: 50,
      api_result: 50,
      api_error: 50,
    },
  },
  rag: {
    LR: {
      query_prompt: 80,
      data_retriever_holder: 100,
      query_rewriter: 70,
      context_enricher: 70,
      llm_processor: 70,
      reranker: 70,
    },
    TB: {
      query_prompt: 40,
      data_retriever_holder: 60,
      query_rewriter: 40,
      context_enricher: 40,
      llm_processor: 40,
      reranker: 40,
    },
  },
  default: {
    LR: {
      data_source: 30,
      data_set: 30,
      data_sink: 30,
      splitter: 30,
    },
    TB: {
      data_source: -80,
      data_set: -80,
      data_sink: -80,
      splitter: -80,
    },
  },
};

export function getRankSpacingForFlowType(
  flowType: string | undefined,
  direction: LayoutDirection,
): RankSpacingByNodeType | null {
  const key = flowType && RANK_SPACING_BY_FLOW_TYPE[flowType] ? flowType : 'default';
  const block = RANK_SPACING_BY_FLOW_TYPE[key];
  if (!block) return null;
  return block[direction] ?? null;
}

/**
 * Flow types whose canvas renderer ignores the layout direction toggle.
 * The toolbar hides the toggle button for these, and the URL query param
 * is also ignored so `?LayoutDirection=TB` cannot bypass the gating.
 */
const FLOW_TYPES_WITHOUT_LAYOUT_TOGGLE: ReadonlySet<string> = new Set(['rag', 'api_server']);

export function flowTypeSupportsLayoutToggle(flowType?: string): boolean {
  return !flowType || !FLOW_TYPES_WITHOUT_LAYOUT_TOGGLE.has(flowType);
}

/**
 * Parse `?LayoutDirection=TB|LR` or `?layoutDirection=...` (case-insensitive).
 * Use this so the pipeline canvas matches the URL on load and when the query changes.
 */
export function layoutDirectionFromSearchParams(search: string): LayoutDirection | null {
  if (search == null || search === '') {
    return null;
  }
  const query = search.startsWith('?') ? search : `?${search}`;
  const params = new URLSearchParams(query);
  const raw = params.get('LayoutDirection') ?? params.get('layoutDirection');
  if (raw == null || raw === '') {
    return null;
  }
  const v = raw.trim().toUpperCase();
  if (v === 'TB') {
    return 'TB';
  }
  if (v === 'LR') {
    return 'LR';
  }
  return null;
}

/** Map Dagre rankdir to canvas LayoutDirection (RL/BT use LR spacing). */
export function rankdirToLayoutDirection(rankdir?: string | null): LayoutDirection {
  return rankdir === 'TB' ? 'TB' : 'LR';
}

export function getDagreSpacingForFlowType(
  flowType: string | undefined,
  layoutDirection: LayoutDirection,
): DagreSpacingConfig {
  const dir = layoutDirection === 'TB' ? 'TB' : 'LR';
  const byFlow = flowType ? FLOW_TYPE_SPACING[flowType] : undefined;
  const block = byFlow ?? DEFAULT_FLOW_TYPE_SPACING;
  return block[dir];
}

/** Helper to get the direction-specific config block */
export function getDirectionConfig(dir: LayoutDirection = 'LR'): DirectionConfig {
  return FLOW_CANVAS_CONFIG[dir] ?? FLOW_CANVAS_CONFIG.LR;
}

// Z-index constants
export const Z_INDEX = {
  CREDENTIAL: 1,
  RESOURCE: 2,
  NEXSET_LOADER: 2,
  FLOW_CANVAS_OVERLAY: 4000,
} as const;

// Node type constants
export const NODE_TYPES = {
  PROJECT: 'project',
  FLOW: 'flow',
  RESOURCE: 'resource',
  NEXSET_LOADER: 'nexsetLoader',
} as const;

// Handle status constants
export const HANDLE_STATUS = {
  RELATION: 'RELATION',
  DEPENDENCY: 'DEPENDENCY',
} as const;

// Special node types
/** React Flow `type` values for Last run / Cron auxiliary nodes */
export const SCHEDULE_CANVAS_NODE_TYPES = ['last_run', 'cron_job'] as const;

export type ScheduleCanvasNodeType = (typeof SCHEDULE_CANVAS_NODE_TYPES)[number];

export function isScheduleCanvasNodeType(nodeType: string | undefined): boolean {
  return nodeType === 'last_run' || nodeType === 'cron_job';
}

export const SPECIAL_NODES = {
  DATA_SINK: 'DATA_SINK',
  SOURCE: 'SOURCE',
  DATA_SET: 'data_set',
  DATA_SOURCE: 'data_source',
  QUERY_RESPONSE: 'query_response',
  CONTEXT_ENRICHER: 'context_enricher',
  LLM_PROCESSOR: 'llm_processor',
  DATA_RETRIEVER_HOLDER: 'data_retriever_holder',
  RESOURCE_COUNT: 'resourceCount',
} as const;
